import { useEffect, useState, useRef } from "react";
import { Loader2, X, Sparkles, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const KIND_LABELS = {
    recategorize: "Ri-cataloga listino con AI",
    ai_parse: "Analisi listino AI",
};

/**
 * Indicatore globale di job in background (ricatalogazione, OCR…).
 * Apparire flottante in basso a destra su tutte le pagine post-login.
 * Polling 5s. Sparisce quando non ci sono job attivi.
 */
export function ActiveJobsIndicator() {
    const { user } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [dismissed, setDismissed] = useState(new Set());
    const [completedRecently, setCompletedRecently] = useState([]);
    const prevJobIdsRef = useRef(new Set());

    useEffect(() => {
        if (!user?.id) return undefined;
        let stopped = false;

        const poll = async () => {
            try {
                const { data } = await api.get("/beverages/jobs/active");
                if (stopped) return;
                const list = data.jobs || [];
                // Rileva job appena completati per il toast verde "fatto"
                const newIds = new Set(list.map((j) => j.id));
                const prev = prevJobIdsRef.current;
                const justFinished = [...prev].filter((id) => !newIds.has(id));
                if (justFinished.length > 0) {
                    setCompletedRecently((cr) => {
                        const next = [...cr, ...justFinished.map((id) => ({ id, ts: Date.now() }))];
                        // mantieni solo gli ultimi 30s
                        return next.filter((e) => Date.now() - e.ts < 30000);
                    });
                }
                prevJobIdsRef.current = newIds;
                setJobs(list);
            } catch {
                // Silent: backend potrebbe rispondere 401 dopo logout
            }
        };
        poll();
        const id = setInterval(poll, 5000);
        return () => { stopped = true; clearInterval(id); };
    }, [user?.id]);

    // Pulisci completedRecently dopo 30s
    useEffect(() => {
        if (completedRecently.length === 0) return undefined;
        const t = setTimeout(() => {
            setCompletedRecently((cr) => cr.filter((e) => Date.now() - e.ts < 30000));
        }, 5000);
        return () => clearTimeout(t);
    }, [completedRecently]);

    const visibleJobs = jobs.filter((j) => !dismissed.has(j.id));
    if (visibleJobs.length === 0 && completedRecently.length === 0) return null;

    return (
        <div className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-40 space-y-2 max-w-[320px] print:hidden" data-testid="active-jobs-indicator">
            {visibleJobs.map((j) => {
                const p = j.progress || {};
                return (
                    <div key={j.id} className="bg-white border border-blue-200 shadow-lg rounded-xl px-4 py-3 animate-fade-up" data-testid={`active-job-${j.id}`}>
                        <div className="flex items-start gap-2">
                            <Loader2 size={14} className="text-blue-500 animate-spin mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-[11px] font-bold text-brand-ink truncate">
                                    {KIND_LABELS[j.kind] || (j.kind ? j.kind : "Analisi AI in corso")}
                                </div>
                                {p.batches_total > 0 ? (
                                    <div className="text-[10px] text-brand-muted mt-0.5">
                                        Batch {p.batches_done || 0} di {p.batches_total}
                                        {p.updated_so_far !== undefined && ` · ${p.updated_so_far} aggiornate`}
                                    </div>
                                ) : p.total > 0 ? (
                                    <div className="text-[10px] text-brand-muted mt-0.5">
                                        Pagina {p.current || 0} di {p.total}
                                        {p.found !== undefined && ` · ${p.found} bevande trovate`}
                                    </div>
                                ) : (
                                    <div className="text-[10px] text-brand-muted mt-0.5">
                                        In elaborazione…
                                    </div>
                                )}
                                {(p.batches_total > 0 || p.total > 0) && (
                                    <div className="mt-1.5 w-full bg-blue-100 rounded-full h-1 overflow-hidden">
                                        <div className="bg-blue-500 h-1 transition-all" style={{
                                            width: `${p.batches_total > 0
                                                ? Math.round(((p.batches_done || 0) / p.batches_total) * 100)
                                                : Math.round(((p.current || 0) / Math.max(1, p.total)) * 100)}%`
                                        }} />
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => setDismissed((d) => new Set([...d, j.id]))}
                                className="text-brand-muted hover:text-brand-ink"
                                aria-label="Nascondi"
                                data-testid={`active-job-dismiss-${j.id}`}
                            >
                                <X size={12} />
                            </button>
                        </div>
                    </div>
                );
            })}
            {completedRecently.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 shadow-lg rounded-xl px-4 py-2.5 flex items-center gap-2 animate-fade-up" data-testid="active-job-completed">
                    <Check size={14} className="text-emerald-600 shrink-0" />
                    <div className="flex-1 text-[11px] font-bold text-emerald-700">
                        <Sparkles size={10} className="inline mr-1" />
                        {completedRecently.length === 1
                            ? "Job AI completato"
                            : `${completedRecently.length} job AI completati`}
                    </div>
                    <button
                        onClick={() => setCompletedRecently([])}
                        className="text-emerald-600 hover:text-emerald-800"
                        aria-label="OK"
                    >
                        <X size={12} />
                    </button>
                </div>
            )}
        </div>
    );
}
